/**
 * FIRE Calculator Service
 *
 * Handles API calls to the FIRE Calculator Lambda backend.
 */

import axios from 'axios';

const API_BASE_URL = 'https://2vqq3i5ycg.execute-api.us-east-2.amazonaws.com/prod';

// ── Types ──

export interface ETFReturns {
  '1m': number;
  '3m': number;
  '6m': number;
  ytd: number;
  '1y': number;
  '3y': number;
  '5y': number;
  '10y': number;
}

export interface ETFInfo {
  symbol: string;
  name: string;
  role: string;
  allocation: number;
  base_return: number;
  best_return: number;
  worst_return: number;
  current_price?: number;
  dividend_yield?: number;
  pe_ratio?: number | null;
  expense_ratio?: number | null;
  market_cap?: number | null;
  beta?: number | null;
  returns?: ETFReturns | null;
}

export interface Strategy {
  id: string;
  name: string;
  description: string;
  risk_level: number;
  etfs: ETFInfo[];
}

export interface CustomETF {
  symbol: string;
  allocation: number;
  base_return: number;
  best_return: number;
  worst_return: number;
}

export interface FireInput {
  fire_target: number;
  current_savings: number;
  annual_contribution: number;
  strategy_id: string;
  contribution_frequency: 'monthly' | 'quarterly' | 'annual';
  custom_allocations?: number[] | null;
  custom_etfs?: CustomETF[] | null;
}

export interface YearProjection {
  year: number;
  balance: number;
  invested: number;
  growth: number;
  annual_contribution: number;
}

export interface ScenarioResult {
  years_to_fire: number | null;
  final_value: number;
  total_invested: number;
  total_gain: number;
  roi_pct: number;
  yearly_projections: YearProjection[];
}

export interface FireSummary {
  base_years: number | null;
  best_years: number | null;
  worst_years: number | null;
  probability_weighted_years: number | null;
  annual_passive_income_at_fire: number;
  monthly_passive_income_at_fire: number;
  safe_withdrawal_rate: number;
}

export interface WeightedReturns {
  base: number;
  best: number;
  worst: number;
}

export interface FireResult {
  strategy: Strategy;
  inputs: {
    fire_target: number;
    current_savings: number;
    annual_contribution: number;
    contribution_frequency: string;
  };
  scenarios: {
    base: ScenarioResult;
    best: ScenarioResult;
    worst: ScenarioResult;
  };
  fire_summary: FireSummary;
  weighted_returns: WeightedReturns;
}

export interface StrategiesResponse {
  strategies: Strategy[];
}

// ── Service ──

class FireCalculatorService {
  async getStrategies(token: string): Promise<StrategiesResponse> {
    const response = await axios.get(`${API_BASE_URL}/fire-strategies`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  async calculate(token: string, input: FireInput): Promise<FireResult> {
    const response = await axios.post(
      `${API_BASE_URL}/fire-calculate`,
      input,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  }

  async healthCheck(): Promise<{ status: string; service: string }> {
    const response = await axios.get(`${API_BASE_URL}/health`);
    return response.data;
  }
}

export const fireCalculatorService = new FireCalculatorService();
